import type { CSSProperties } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { cn } from "@/lib/utils";

/** The shortest side a dragged box can have, in displayed pixels. */
const MIN_CROP_SIDE_PX = 32;

/** A crop box in fractions (0 to 1) of the image width and height. */
export interface ImageCropBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The largest centered box with the aspect (width ÷ height); the whole image without one. */
export const centeredCropBox = (
  aspect: number | undefined,
  image: { width: number; height: number },
): ImageCropBox => {
  const imageAspect = image.width / image.height;
  if (aspect == null || !(imageAspect > 0)) {
    return { left: 0, top: 0, width: 1, height: 1 };
  }
  const width = aspect < imageAspect ? aspect / imageAspect : 1;
  const height = aspect < imageAspect ? 1 : imageAspect / aspect;
  return { left: (1 - width) / 2, top: (1 - height) / 2, width, height };
};

export interface ImageCropperProps {
  src: string;
  alt: string;
  /** Locks the box to width ÷ height, for example 3 for a 3:1 banner; free when unset. */
  aspect?: number;
  box: ImageCropBox;
  onBoxChange: (box: ImageCropBox) => void;
  /** Natural pixel size of the loaded image. */
  onImageLoad?: (size: { width: number; height: number }) => void;
  className?: string;
  /**
   * Draws the image this much smaller, around its centre, without changing the space it
   * takes; dragging still works, because the crop reads the drawn size.
   */
  scale?: number;
}

/** Drag or resize a box over an image to choose a crop. */
export const ImageCropper = ({
  src,
  alt,
  aspect,
  box,
  onBoxChange,
  onImageLoad,
  className,
  scale = 1,
}: ImageCropperProps) => (
  <ReactCrop
    crop={{
      unit: "%",
      x: box.left * 100,
      y: box.top * 100,
      width: box.width * 100,
      height: box.height * 100,
    }}
    aspect={aspect}
    keepSelection
    ruleOfThirds
    // react-image-crop derives one side from the other under an aspect, so scale both minimums.
    minWidth={MIN_CROP_SIDE_PX * Math.max(1, aspect ?? 1)}
    minHeight={MIN_CROP_SIDE_PX * Math.max(1, 1 / (aspect ?? 1))}
    onChange={(_, percent) =>
      onBoxChange({
        left: percent.x / 100,
        top: percent.y / 100,
        width: percent.width / 100,
        height: percent.height / 100,
      })
    }
    className={cn("max-h-[min(55vh,520px)] max-w-full", className)}
    style={
      scale !== 1
        ? ({
            transform: `scale(${scale})`,
            // The transform shrinks the handles too; keep them their usual size on screen.
            "--rc-drag-handle-size": `${12 / scale}px`,
            "--rc-drag-handle-mobile-size": `${24 / scale}px`,
            "--rc-drag-bar-size": `${6 / scale}px`,
          } as CSSProperties)
        : undefined
    }
  >
    <img
      src={src}
      alt={alt}
      onLoad={(event) =>
        onImageLoad?.({
          width: event.currentTarget.naturalWidth,
          height: event.currentTarget.naturalHeight,
        })
      }
    />
  </ReactCrop>
);
